/**
 * Local-dev identity passing.
 *
 * The local API server (lixani-dynamodb-api/3-API, `npm start`) runs as plain
 * Koa with no Lambda event, so it has no API Gateway IAM authorizer to derive
 * the caller from and cannot tell who is calling. It therefore accepts an
 * X-Dev-User-Id header instead — see its src/server/middlewares/dev-identity.ts.
 *
 * We can supply that honestly: `getUser.service.js` is hardcoded to the
 * *deployed* API, so by the time anything talks to localhost we already know
 * our real Cognito identity id. Two browser profiles are then two real,
 * separately signed-in users rather than one shared hardcoded id — which is
 * what Chime needs, since it treats one id joining twice as one person
 * switching devices and evicts the first attendee.
 *
 * Two independent conditions keep this out of production: the whole module is
 * inert in a production build (so `npm run build` strips it), and the header is
 * only ever attached to localhost URLs.
 */

import axios from 'axios';

// Deliberately the same predicate as isDevBuild in utils/localApi.js, which is
// what decides whether a feature talks to localhost at all. This must never be
// stricter than that one: any build where localApi routes to localhost but this
// goes inert would send unidentified requests, and the server would silently
// fall back to its DEV_USER_ID - acting as the wrong person, which is the exact
// confusion this exists to remove.
const IS_DEV = process.env.NODE_ENV !== 'production';
const STORAGE_KEY = 'lixani.devUserId';

// Cached in sessionStorage (per-tab, so it cannot bleed between profiles) so a
// page reload has the identity synchronously, before loadUser resolves.
const readCached = () => {
  if (!IS_DEV) return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

let devUserId = readCached();

export const setDevIdentity = (userId) => {
  if (!IS_DEV || !userId) return;
  devUserId = userId;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, userId);
  } catch (e) {
    // Private mode / storage disabled: in-memory value still works.
  }
};

const isLocalUrl = (url) =>
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(url || '');

/**
 * Returns the request with X-Dev-User-Id attached, but only for localhost.
 * Anything aimed at a deployed host is returned untouched, so production
 * signatures and headers are unaffected.
 */
export const withDevIdentity = (request) => {
  if (!IS_DEV || !devUserId || !request || !isLocalUrl(request.url)) {
    return request;
  }

  return {
    ...request,
    headers: {
      ...request.headers,
      'X-Dev-User-Id': devUserId,
    },
  };
};

// Catch-all for the ~44 services that sign inline with aws4 instead of going
// through utils/createSignedRequest. Without this they would fall back to the
// server's DEV_USER_ID and silently act as the wrong person in a two-user
// session. Registered here rather than in src/index.js, which is a collision
// hot spot; this module is pulled in by createSignedRequest, so it loads with
// the first service import. No axios.create instances exist in the app, so the
// default instance covers every request.
if (IS_DEV) {
  axios.interceptors.request.use((config) => {
    if (!devUserId || !isLocalUrl(config.url)) return config;

    config.headers = { ...config.headers, 'X-Dev-User-Id': devUserId };
    return config;
  });
}
